import React, { useState } from 'react';

const EXPENSE_FIELDS = [
  { key: "mealAllowance",  label: "Meal Allowance",  isExpense: true  },
  { key: "transportation", label: "Transportation",  isExpense: true  },
  { key: "electricity",    label: "Electricity",     isExpense: false },
  { key: "internet",       label: "Internet",        isExpense: true  },
  { key: "salary",         label: "Salary",          isExpense: true  },
  { key: "sss",            label: "SSS Deduction",   isExpense: false },
  { key: "philhealth",     label: "PhilHealth",      isExpense: false },
  { key: "hdmf",           label: "HDMF",            isExpense: false },
  { key: "others",         label: "Others / Misc",   isExpense: true  },
];

const fmt = (n) => `₱${Number(n||0).toLocaleString('en-PH',{minimumFractionDigits:2,maximumFractionDigits:2})}`;
const pct = (n,t) => t > 0 ? `${((n/t)*100).toFixed(1)}%` : '0.0%';
const sumExp = (exp) => EXPENSE_FIELDS.filter(f => f.isExpense).reduce((s,f) => s + Number(exp?.[f.key]||0), 0);

function downloadCSV(content, filename) {
  const blob = new Blob([content], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

export default function ReportsTab({ state, selectedBranchId }) {
  const now = new Date();
  const [activeReport, setActiveReport] = useState('pl');
  const [fromDate, setFromDate] = useState(
    `${now.getFullYear()}-01-01`
  );
  const [toDate, setToDate] = useState(now.toISOString().slice(0,10));
  const [filterBranch, setFilterBranch] = useState('all');

  const availBranches = state.branches.filter(b => !b.isMain);

  // Filter records
  const filtered = state.dailyRecords.filter(r => {
    const branchMatch = filterBranch === 'all' || r.branchId === filterBranch;
    const dateMatch = r.date >= fromDate && r.date <= toDate;
    return branchMatch && dateMatch;
  });

  const reports = [
    { id: 'pl',       label: '📊 Profit & Loss'         },
    { id: 'monthly',  label: '📅 Monthly Sales Summary'  },
    { id: 'payroll',  label: '👥 Payroll Report'         },
    { id: 'expenses', label: '💸 Expense Breakdown'      },
  ];

  return (
    <div>
      {/* Report Selector */}
      <div style={S.reportNav}>
        {reports.map(r => (
          <button
            key={r.id}
            onClick={() => setActiveReport(r.id)}
            style={{ ...S.reportBtn, ...(activeReport === r.id ? S.reportBtnActive : {}) }}
          >
            {r.label}
          </button>
        ))}
      </div>

      {/* Filters */}
      <div style={S.filterBar}>
        <div style={S.filterGroup}>
          <label style={S.label}>From</label>
          <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} style={S.input} />
        </div>
        <div style={S.filterGroup}>
          <label style={S.label}>To</label>
          <input type="date" value={toDate} onChange={e => setToDate(e.target.value)} style={S.input} />
        </div>
        <div style={S.filterGroup}>
          <label style={S.label}>Branch</label>
          <select value={filterBranch} onChange={e => setFilterBranch(e.target.value)} style={S.input}>
            <option value="all">All Branches</option>
            {availBranches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
        </div>
      </div>

      {/* Reports */}
      {activeReport === 'pl'       && <PLReport       records={filtered} state={state} fromDate={fromDate} toDate={toDate} filterBranch={filterBranch} />}
      {activeReport === 'monthly'  && <MonthlyReport  records={filtered} state={state} />}
      {activeReport === 'payroll'  && <PayrollReport  state={state} filterBranch={filterBranch} />}
      {activeReport === 'expenses' && <ExpenseReport  records={filtered} fromDate={fromDate} toDate={toDate} />}
    </div>
  );
}

// ─── PROFIT & LOSS ───────────────────────────────────────────────────────────
function PLReport({ records, state, fromDate, toDate, filterBranch }) {
  const totalSales    = records.reduce((s,r) => s + Number(r.sales||0), 0);
  const totalCost     = records.reduce((s,r) => s + Number(r.cost||0), 0);
  const totalExpenses = records.reduce((s,r) => s + sumExp(r.expenses), 0);
  const grossProfit   = totalSales - totalCost;
  const netProfit     = grossProfit - totalExpenses;
  const grossMargin   = totalSales > 0 ? (grossProfit/totalSales)*100 : 0;
  const netMargin     = totalSales > 0 ? (netProfit/totalSales)*100 : 0;

  const branchName = filterBranch === 'all' ? 'All Branches' :
    state.branches.find(b => b.id === filterBranch)?.name || filterBranch;

  function exportCSV() {
    const rows = [
      ['Profit & Loss Statement'],
      ['Period', `${fromDate} to ${toDate}`],
      ['Branch', branchName],
      [''],
      ['Item', 'Amount', '% of Sales'],
      ['Gross Sales', totalSales, '100.0%'],
      ['Cost of Sales', totalCost, pct(totalCost, totalSales)],
      ['Gross Profit', grossProfit, pct(grossProfit, totalSales)],
      ['Total Expenses', totalExpenses, pct(totalExpenses, totalSales)],
      ['Net Profit / Loss', netProfit, pct(netProfit, totalSales)],
    ].map(r => r.join(',')).join('\n');
    downloadCSV(rows, `profit-loss-${fromDate}-${toDate}.csv`);
  }

  return (
    <div style={S.card}>
      <div style={S.cardHeader}>
        <div>
          <h3 style={S.cardTitle}>📊 Profit & Loss Statement</h3>
          <p style={S.subtitle}>{fromDate} to {toDate} · {branchName}</p>
        </div>
        <button onClick={exportCSV} style={S.exportBtn}>⬇️ Export CSV</button>
      </div>

      <table style={S.table}>
        <thead>
          <tr>
            <th style={S.th}>Item</th>
            <th style={{...S.th, textAlign:'right'}}>Amount</th>
            <th style={{...S.th, textAlign:'right'}}>% of Sales</th>
          </tr>
        </thead>
        <tbody>
          {[
            { label: 'Gross Sales',       value: totalSales,    pctVal: 100,                         bold: false, color: '#1e293b' },
            { label: 'Cost of Sales',     value: -totalCost,    pctVal: (totalCost/totalSales)*100,   bold: false, color: '#dc2626' },
            { label: 'Gross Profit',      value: grossProfit,   pctVal: grossMargin,                  bold: true,  color: grossProfit>=0?'#16a34a':'#dc2626', divider: true },
            { label: 'Total Expenses',    value: -totalExpenses,pctVal: (totalExpenses/totalSales)*100,bold: false, color: '#d97706' },
            { label: 'Net Profit / Loss', value: netProfit,     pctVal: netMargin,                    bold: true,  color: netProfit>=0?'#16a34a':'#dc2626', divider: true },
          ].map((row, i) => (
            <tr key={i} style={{ background: row.divider ? '#f8fafc' : 'white' }}>
              <td style={{...S.td, fontWeight: row.bold?700:400, paddingLeft: row.divider?16:32}}>{row.label}</td>
              <td style={{...S.td, textAlign:'right', fontWeight: row.bold?700:400, color: row.color}}>{fmt(Math.abs(row.value))}{row.value < 0 ? ' (-)' : ''}</td>
              <td style={{...S.td, textAlign:'right', color:'#64748b'}}>{isNaN(row.pctVal)?'—':`${Number(row.pctVal).toFixed(1)}%`}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Summary KPIs */}
      <div style={S.kpiRow}>
        {[
          { label: 'Gross Margin',  value: `${grossMargin.toFixed(1)}%`,  color: '#4f46e5' },
          { label: 'Net Margin',    value: `${netMargin.toFixed(1)}%`,    color: netProfit>=0?'#16a34a':'#dc2626' },
          { label: 'Total Records', value: records.length,                color: '#0891b2' },
        ].map(k => (
          <div key={k.label} style={S.kpiBox}>
            <div style={{...S.kpiVal, color: k.color}}>{k.value}</div>
            <div style={S.kpiLabel}>{k.label}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── MONTHLY SALES SUMMARY ───────────────────────────────────────────────────
function MonthlyReport({ records, state }) {
  // Group by month
  const map = {};
  records.forEach(r => {
    const month = r.date.slice(0,7);
    if (!map[month]) map[month] = { sales:0, cost:0, expenses:0, count:0 };
    map[month].sales    += Number(r.sales||0);
    map[month].cost     += Number(r.cost||0);
    map[month].expenses += sumExp(r.expenses);
    map[month].count++;
  });
  const rows = Object.entries(map).sort((a,b) => a[0].localeCompare(b[0]));
  const totals = rows.reduce((acc,[,v]) => ({
    sales: acc.sales+v.sales, cost: acc.cost+v.cost,
    expenses: acc.expenses+v.expenses, count: acc.count+v.count
  }), { sales:0, cost:0, expenses:0, count:0 });

  function exportCSV() {
    const header = 'Month,Sales,Cost,Expenses,Net Profit,Records\n';
    const body = rows.map(([month, v]) => {
      const net = v.sales - v.cost - v.expenses;
      return `${month},${v.sales},${v.cost},${v.expenses},${net},${v.count}`;
    }).join('\n');
    downloadCSV(header+body, `monthly-sales-summary.csv`);
  }

  return (
    <div style={S.card}>
      <div style={S.cardHeader}>
        <h3 style={S.cardTitle}>📅 Monthly Sales Summary</h3>
        <button onClick={exportCSV} style={S.exportBtn}>⬇️ Export CSV</button>
      </div>
      <div style={{overflowX:'auto'}}>
        <table style={S.table}>
          <thead>
            <tr>
              {['Month','Gross Sales','Cost','Expenses','Net Profit','Records'].map(h => (
                <th key={h} style={{...S.th, textAlign: h==='Month'?'left':'right'}}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map(([month, v], i) => {
              const net = v.sales - v.cost - v.expenses;
              return (
                <tr key={month} style={{ background: i%2===0?'white':'#f8fafc' }}>
                  <td style={{...S.td, fontWeight:600}}>{new Date(month+'-01').toLocaleString('en',{month:'long',year:'numeric'})}</td>
                  <td style={{...S.td, textAlign:'right'}}>{fmt(v.sales)}</td>
                  <td style={{...S.td, textAlign:'right', color:'#0891b2'}}>{fmt(v.cost)}</td>
                  <td style={{...S.td, textAlign:'right', color:'#d97706'}}>{fmt(v.expenses)}</td>
                  <td style={{...S.td, textAlign:'right', fontWeight:600, color:net>=0?'#16a34a':'#dc2626'}}>{fmt(net)}</td>
                  <td style={{...S.td, textAlign:'right', color:'#64748b'}}>{v.count}</td>
                </tr>
              );
            })}
            {/* Totals row */}
            <tr style={{ background:'#1e1b4b' }}>
              <td style={{...S.td, color:'white', fontWeight:700}}>TOTAL</td>
              <td style={{...S.td, textAlign:'right', color:'white', fontWeight:700}}>{fmt(totals.sales)}</td>
              <td style={{...S.td, textAlign:'right', color:'#93c5fd', fontWeight:700}}>{fmt(totals.cost)}</td>
              <td style={{...S.td, textAlign:'right', color:'#fcd34d', fontWeight:700}}>{fmt(totals.expenses)}</td>
              <td style={{...S.td, textAlign:'right', fontWeight:700, color:(totals.sales-totals.cost-totals.expenses)>=0?'#86efac':'#fca5a5'}}>{fmt(totals.sales-totals.cost-totals.expenses)}</td>
              <td style={{...S.td, textAlign:'right', color:'#c7d2fe', fontWeight:700}}>{totals.count}</td>
            </tr>
          </tbody>
        </table>
        {rows.length === 0 && <p style={S.empty}>No records found for selected period.</p>}
      </div>
    </div>
  );
}

// ─── PAYROLL REPORT ──────────────────────────────────────────────────────────
function PayrollReport({ state, filterBranch }) {
  const employees = state.employees.filter(e =>
    filterBranch === 'all' || e.branchId === filterBranch
  );

  const totals = employees.reduce((acc, e) => ({
    salary:     acc.salary     + e.monthlySalary,
    allowances: acc.allowances + e.mealAllowance + e.otherAllowances,
    deductions: acc.deductions + e.sssDeduction + e.philhealthDeduction + e.hdmfDeduction + e.otherDeductions,
  }), { salary:0, allowances:0, deductions:0 });

  function exportCSV() {
    const header = 'Name,Branch,Status,Daily Salary,Meal Allowance,Other Allowances,SSS,PhilHealth,HDMF,Other Deductions,Net Pay\n';
    const body = employees.map(e => {
      const branch = state.branches.find(b => b.id === e.branchId)?.name || '';
      const gross = e.monthlySalary + e.mealAllowance + e.otherAllowances;
      const ded = e.sssDeduction + e.philhealthDeduction + e.hdmfDeduction + e.otherDeductions;
      return `"${e.name}","${branch}",${e.status},${e.monthlySalary},${e.mealAllowance},${e.otherAllowances},${e.sssDeduction},${e.philhealthDeduction},${e.hdmfDeduction},${e.otherDeductions},${gross-ded}`;
    }).join('\n');
    downloadCSV(header+body, `payroll-report.csv`);
  }

  return (
    <div style={S.card}>
      <div style={S.cardHeader}>
        <h3 style={S.cardTitle}>👥 Employee Payroll Report</h3>
        <button onClick={exportCSV} style={S.exportBtn}>⬇️ Export CSV</button>
      </div>

      {/* Payroll KPIs */}
      <div style={S.kpiRow}>
        {[
          { label: 'Total Employees',  value: employees.length,                                    color: '#4f46e5' },
          { label: 'Active',           value: employees.filter(e=>e.status==='Active').length,     color: '#16a34a' },
          { label: 'Total Payroll',    value: fmt(totals.salary + totals.allowances),              color: '#0891b2' },
          { label: 'Total Deductions', value: fmt(totals.deductions),                              color: '#d97706' },
          { label: 'Net Payroll',      value: fmt(totals.salary + totals.allowances - totals.deductions), color: '#7c3aed' },
        ].map(k => (
          <div key={k.label} style={S.kpiBox}>
            <div style={{...S.kpiVal, color:k.color}}>{k.value}</div>
            <div style={S.kpiLabel}>{k.label}</div>
          </div>
        ))}
      </div>

      <div style={{overflowX:'auto'}}>
        <table style={S.table}>
          <thead>
            <tr>
              {['Name','Branch','Status','Salary','Allowances','Deductions','Net Pay'].map(h => (
                <th key={h} style={{...S.th, textAlign:h==='Name'||h==='Branch'||h==='Status'?'left':'right'}}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {employees.map((e, i) => {
              const branch = state.branches.find(b => b.id === e.branchId)?.name || '';
              const allowances = e.mealAllowance + e.otherAllowances;
              const deductions = e.sssDeduction + e.philhealthDeduction + e.hdmfDeduction + e.otherDeductions;
              const netPay = e.monthlySalary + allowances - deductions;
              return (
                <tr key={e.id} style={{ background: i%2===0?'white':'#f8fafc' }}>
                  <td style={{...S.td, fontWeight:600}}>{e.name}</td>
                  <td style={S.td}>{branch}</td>
                  <td style={S.td}>
                    <span style={{...S.badge, background:e.status==='Active'?'#dcfce7':'#fee2e2', color:e.status==='Active'?'#16a34a':'#dc2626'}}>
                      {e.status}
                    </span>
                  </td>
                  <td style={{...S.td, textAlign:'right'}}>{fmt(e.monthlySalary)}</td>
                  <td style={{...S.td, textAlign:'right', color:'#16a34a'}}>{fmt(allowances)}</td>
                  <td style={{...S.td, textAlign:'right', color:'#dc2626'}}>{fmt(deductions)}</td>
                  <td style={{...S.td, textAlign:'right', fontWeight:700, color:'#4f46e5'}}>{fmt(netPay)}</td>
                </tr>
              );
            })}
            {/* Totals */}
            <tr style={{ background:'#1e1b4b' }}>
              <td style={{...S.td, color:'white', fontWeight:700}} colSpan={3}>TOTAL</td>
              <td style={{...S.td, textAlign:'right', color:'white', fontWeight:700}}>{fmt(totals.salary)}</td>
              <td style={{...S.td, textAlign:'right', color:'#86efac', fontWeight:700}}>{fmt(totals.allowances)}</td>
              <td style={{...S.td, textAlign:'right', color:'#fca5a5', fontWeight:700}}>{fmt(totals.deductions)}</td>
              <td style={{...S.td, textAlign:'right', color:'#c7d2fe', fontWeight:700}}>{fmt(totals.salary+totals.allowances-totals.deductions)}</td>
            </tr>
          </tbody>
        </table>
        {employees.length === 0 && <p style={S.empty}>No employees found.</p>}
      </div>
    </div>
  );
}

// ─── EXPENSE BREAKDOWN ───────────────────────────────────────────────────────
function ExpenseReport({ records, fromDate, toDate }) {
  const totalSales = records.reduce((s,r) => s + Number(r.sales||0), 0);

  const expTotals = EXPENSE_FIELDS.filter(f => f.isExpense).map(f => ({
    label: f.label,
    total: records.reduce((s,r) => s + Number(r.expenses?.[f.key]||0), 0),
  })).sort((a,b) => b.total - a.total);

  const grandTotal = expTotals.reduce((s,e) => s + e.total, 0);

  function exportCSV() {
    const header = 'Expense Category,Total Amount,% of Sales,% of Total Expenses\n';
    const body = expTotals.map(e =>
      `"${e.label}",${e.total},${totalSales>0?((e.total/totalSales)*100).toFixed(2)+'%':'—'},${grandTotal>0?((e.total/grandTotal)*100).toFixed(2)+'%':'—'}`
    ).join('\n');
    downloadCSV(header+body, `expense-breakdown-${fromDate}-${toDate}.csv`);
  }

  return (
    <div style={S.card}>
      <div style={S.cardHeader}>
        <div>
          <h3 style={S.cardTitle}>💸 Expense Breakdown Report</h3>
          <p style={S.subtitle}>{fromDate} to {toDate}</p>
        </div>
        <button onClick={exportCSV} style={S.exportBtn}>⬇️ Export CSV</button>
      </div>

      <div style={{overflowX:'auto'}}>
        <table style={S.table}>
          <thead>
            <tr>
              {['Expense Category','Total Amount','% of Sales','% of Total Expenses'].map(h => (
                <th key={h} style={{...S.th, textAlign:h==='Expense Category'?'left':'right'}}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {expTotals.map((e, i) => (
              <tr key={e.label} style={{ background: i%2===0?'white':'#f8fafc' }}>
                <td style={{...S.td, fontWeight:500}}>{e.label}</td>
                <td style={{...S.td, textAlign:'right'}}>{fmt(e.total)}</td>
                <td style={{...S.td, textAlign:'right', color:'#64748b'}}>{pct(e.total, totalSales)}</td>
                <td style={S.td}>
                  <div style={{display:'flex', alignItems:'center', gap:8, justifyContent:'flex-end'}}>
                    <div style={{width:80, background:'#e2e8f0', borderRadius:4, height:8}}>
                      <div style={{width:`${grandTotal>0?(e.total/grandTotal)*100:0}%`, background:'#4f46e5', borderRadius:4, height:8}}/>
                    </div>
                    <span style={{minWidth:45, textAlign:'right'}}>{pct(e.total, grandTotal)}</span>
                  </div>
                </td>
              </tr>
            ))}
            <tr style={{ background:'#1e1b4b' }}>
              <td style={{...S.td, color:'white', fontWeight:700}}>TOTAL EXPENSES</td>
              <td style={{...S.td, textAlign:'right', color:'white', fontWeight:700}}>{fmt(grandTotal)}</td>
              <td style={{...S.td, textAlign:'right', color:'#fcd34d', fontWeight:700}}>{pct(grandTotal, totalSales)}</td>
              <td style={{...S.td, textAlign:'right', color:'#c7d2fe', fontWeight:700}}>100.0%</td>
            </tr>
          </tbody>
        </table>
        {records.length === 0 && <p style={S.empty}>No records found for selected period.</p>}
      </div>
    </div>
  );
}

// ─── STYLES ──────────────────────────────────────────────────────────────────
const S = {
  reportNav: { display:'flex', gap:8, flexWrap:'wrap', marginBottom:16 },
  reportBtn: { background:'#f1f5f9', border:'1px solid #e2e8f0', borderRadius:8, padding:'10px 16px', cursor:'pointer', fontSize:14, fontWeight:600, color:'#475569' },
  reportBtnActive: { background:'#4f46e5', color:'white', borderColor:'#4f46e5' },
  filterBar: { background:'white', borderRadius:12, padding:'16px 20px', marginBottom:20, display:'flex', gap:16, flexWrap:'wrap', boxShadow:'0 1px 3px rgba(0,0,0,.08)' },
  filterGroup: { display:'flex', flexDirection:'column', gap:4 },
  label: { fontSize:12, fontWeight:600, color:'#374151' },
  input: { border:'1px solid #d1d5db', borderRadius:8, padding:'8px 10px', fontSize:14, outline:'none', background:'#f9fafb' },
  card: { background:'white', borderRadius:12, padding:24, marginBottom:20, boxShadow:'0 1px 3px rgba(0,0,0,.1)' },
  cardHeader: { display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:20, flexWrap:'wrap', gap:12 },
  cardTitle: { margin:'0 0 4px', fontSize:17, fontWeight:700, color:'#1e293b' },
  subtitle: { margin:0, fontSize:13, color:'#64748b' },
  exportBtn: { background:'#f1f5f9', border:'1px solid #e2e8f0', borderRadius:8, padding:'8px 14px', cursor:'pointer', fontSize:13, fontWeight:600, color:'#374151' },
  table: { width:'100%', borderCollapse:'collapse', fontSize:13 },
  th: { background:'#f8fafc', padding:'10px 12px', fontWeight:600, borderBottom:'2px solid #e2e8f0', color:'#374151', whiteSpace:'nowrap' },
  td: { padding:'10px 12px', borderBottom:'1px solid #f1f5f9', verticalAlign:'middle' },
  badge: { padding:'2px 8px', borderRadius:12, fontSize:12, fontWeight:600 },
  kpiRow: { display:'flex', gap:12, flexWrap:'wrap', marginBottom:20 },
  kpiBox: { background:'#f8fafc', borderRadius:10, padding:'12px 20px', textAlign:'center', minWidth:100, border:'1px solid #e2e8f0' },
  kpiVal: { fontSize:18, fontWeight:700, marginBottom:2 },
  kpiLabel: { fontSize:11, color:'#64748b', fontWeight:500 },
  empty: { textAlign:'center', color:'#94a3b8', padding:'40px 0', fontSize:14 },
};
